import {
  differenceInCalendarDays,
  startOfDay,
} from 'date-fns';
import {
  CompetitionFormat,
  PortalCompetition,
  PortalCompletedCompetition,
  PortalRankingEntry,
  RankingMethod,
} from '@api/competitionsApi/interfaces';
import { ROUTES } from '@routes/routes';
import { translate } from '@translations/i18n';
import { RankingItem } from '@pages/Portal/Competitions/components/RankingTable/interfaces';
import { TRank } from '@components/content-side-bar/RankList/interfaces';
import { ICompetition } from './Overview/components/Competition/interfaces';
import { ICompletedCompetition } from './Results/Overview/components/CompletedCompetition/interfaces';

export const mapPortalCompetitionToCard = (
  competition: PortalCompetition
): ICompetition => ({
  slug: competition.slug,
  title: competition.title,
  discipline: competition.discipline.name,
  weaponGroups: competition.discipline.weaponGroups ?? [],
  format: competition.format,
  status: competition.status === 'open' ? 'open' : 'closed',
  description: competition.description ?? '',
  imgSrc: competition.coverImage
    ? ROUTES.images.image(competition.coverImage)
    : '',
  startDate: competition.startsOn ? new Date(competition.startsOn) : new Date(),
  endDate: competition.endsOn ? new Date(competition.endsOn) : new Date(),
  enrollmentCount: competition.enrollmentCount,
  enrolled: competition.enrolled,
});

export const mapPortalCompletedCompetitionToCard = (
  competition: PortalCompletedCompetition,
  detailsRoute: string
): ICompletedCompetition => ({
  ...mapPortalCompetitionToCard(competition),
  detailsRoute,
  podium: competition.podium ?? [],
});

export const getCompetitionFormatName = (
  format: CompetitionFormat
): string => {
  return translate(`competitions:manage.labels.format.${format}`);
};

export const getCompetitionStatusName = (
  status: ICompetition['status']
): string => {
  return translate(`competitions:portal.labels.status.${status}`);
};

export const mapPortalRankingEntryToRankingItem = (
  entry: PortalRankingEntry
): RankingItem => ({
  id: entry.member.slug,
  rank: entry.rank,
  name: entry.member.fullName,
  avatarSource: ROUTES.images.avatar(entry.member.slug),
  weaponGroups: entry.weaponGroups ?? [],
  totalSubmitted: entry.submissionCount,
  totalScore: entry.rankingScore,
  positionChange: entry.positionChange,
  previousRank: entry.previousRank,
});

export const mapPortalRankingEntryToRank = (
  entry: PortalRankingEntry
): TRank => ({
  position: entry.rank as number,
  name: entry.member.fullName,
  avatarSrc: ROUTES.images.avatar(entry.member.slug),
  score: entry.rankingScore as number,
});

export const getRankingScoreColumnLabel = (
  rankingMethod: RankingMethod
): string => {
  return getRankingValueColumnLabel('points', rankingMethod);
};

export const getRankingValueColumnLabel = (
  scoreUnit: 'points' | 'time',
  rankingMethod: RankingMethod
): string => {
  const columnKey =
    scoreUnit === 'time' ? 'timeColumn' : 'scoreColumn';

  return translate(
    `competitions:portal.ranking.${columnKey}.${rankingMethod}`
  );
};

export const getParticipantRankingValueColumnLabel = (
  scoreUnit: 'points' | 'time'
): string => {
  if (scoreUnit === 'time') {
    return translate('competitions:manage.labels.scoreUnit.time');
  }

  return translate('competitions:manage.details.participants.columns.score');
};

export const getRankingSubmittedCountColumnLabel = (
  format: CompetitionFormat
): string => {
  return translate(
    format === 'session'
      ? 'competitions:portal.ranking.columns.seriesCount'
      : 'competitions:portal.ranking.columns.submissionCount'
  );
};

export const getRankingSubmittedCountColumnTooltip = (
  format: CompetitionFormat
): string => {
  return translate(
    format === 'session'
      ? 'competitions:rankingRules.columnProgressTooltip.session'
      : 'competitions:rankingRules.columnProgressTooltip.self_paced'
  );
};

export const getRankingPositionChangeLabel = (
  rank: number,
  previousRank: number,
  positionChange: RankingItem['positionChange']
): string | null => {
  const delta = Math.abs(previousRank - rank);

  if (delta === 0) {
    return null;
  }

  if (positionChange === 'up') {
    return `+${delta}`;
  }

  if (positionChange === 'down') {
    return `-${delta}`;
  }

  return null;
};

export const getCompetitionDurationProgress = (
  startDate: Date,
  endDate: Date,
  today: Date = new Date()
): number => {
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);
  const now = startOfDay(today);

  if (now <= start) {
    return 0;
  }

  if (now >= end) {
    return 100;
  }

  const totalDays = differenceInCalendarDays(end, start);

  if (totalDays <= 0) {
    return 100;
  }

  const elapsedDays = differenceInCalendarDays(now, start);

  return Math.min(
    100,
    Math.max(0, Math.round((elapsedDays / totalDays) * 100))
  );
};
